import type {
  TCustomerSupportButtonBase,
  TCustomerSupportProvider,
} from '../../types/thirdPartyAppSettings';
import type { TGorgiasSupportButtonSetting } from '../../types/thirdPartyAppSettings/properties';
import { handleGorgiasSupport } from './providers';
import type { ProviderRenderer } from './types';

// Registry mapping support providers to their renderer functions
const providerRegistry = new Map<TCustomerSupportProvider, ProviderRenderer>();

export function registerSupportProvider(
  provider: TCustomerSupportProvider,
  renderer: ProviderRenderer
) {
  providerRegistry.set(provider, renderer);
}

/**
 * Renders the support button in the specified container
 */
export function renderSupportButton(
  container: HTMLElement,
  config: TCustomerSupportButtonBase<TCustomerSupportProvider>,
  callback?: () => void,
  customClass = ''
): HTMLElement | null {
  if (!container) return null;
  const renderer = providerRegistry.get(config.provider);
  if (!renderer) return null;
  const buttonElement = renderer(config, customClass, callback);
  if (buttonElement) container.appendChild(buttonElement);
  return buttonElement;
}

registerSupportProvider('gorgias', (cfg, customClass = '', callback?: () => void) => {
  if (!window.GorgiasChat || typeof window.GorgiasChat.open !== 'function') {
    console.warn('[Boost SD] Gorgias chat widget is not available');
    return null;
  }

  const settings = (cfg.buttonSettings || {}) as TGorgiasSupportButtonSetting;
  const btn = document.createElement('button');
  const styleClass = settings.buttonStyle || 'plain-button';
  btn.className =
    `boost-sd__support-button boost-sd__support-button--${styleClass} ${customClass}`.trim();
  btn.setAttribute('type', 'button');
  btn.setAttribute('data-provider', cfg.provider);
  btn.setAttribute('aria-label', settings.buttonText || 'Support');

  if (settings.textColor)
    btn.style.setProperty('--boost-sd-support-btn-text-color', settings.textColor);
  if (settings.backgroundColor)
    btn.style.setProperty('--boost-sd-support-btn-bg-color', settings.backgroundColor);
  if (settings.borderColor)
    btn.style.setProperty('--boost-sd-support-btn-border-color', settings.borderColor);

  if (settings.buttonText) {
    const textSpan = document.createElement('span');
    textSpan.className = 'boost-sd__support-button-text';
    textSpan.textContent = settings.buttonText;
    btn.appendChild(textSpan);
  }

  btn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      handleGorgiasSupport();
      if (callback) callback();
    } catch (e) {
      console.warn('[Boost SD] Support button click failed', e);
    }

    window.dispatchEvent(
      new CustomEvent('boost-sd:support-button-clicked', {
        detail: {
          provider: cfg.provider,
          buttonText: (settings && settings.buttonText) || undefined,
        },
      })
    );
  });

  return btn;
});
